import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../lib/auth";
import AppHeader from "../components/AppHeader";
import Markdown from "../components/Markdown";
import AssignmentForm from "../components/AssignmentForm";
import { getMarkdown, getAssignment } from "../content";
import {
  fetchLesson,
  fetchModuleLessons,
  fetchLessonProgress,
  markLessonComplete,
  unmarkLessonComplete,
  type LessonWithModule,
} from "../lib/lessons";
import type { EnrollmentRow, LessonRow } from "../lib/types";

// Reader view for a single lesson. Content lessons render Markdown; workbook/
// assignment lessons render the question form. Provides prev/next within the
// module and a mark-complete affordance for content lessons.
export default function Lesson() {
  const { lessonId } = useParams<{ lessonId: string }>();
  const { student } = useAuth();
  const studentId = student?.id ?? null;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lesson, setLesson] = useState<LessonWithModule | null>(null);
  const [siblings, setSiblings] = useState<LessonRow[]>([]);
  const [enrollment, setEnrollment] = useState<EnrollmentRow | null>(null);
  const [completedAt, setCompletedAt] = useState<string | null>(null);
  const [savingProgress, setSavingProgress] = useState(false);

  const load = useCallback(async () => {
    if (!lessonId) return;
    setLoading(true);
    setError(null);
    try {
      const l = await fetchLesson(lessonId);
      if (!l) {
        setError("Lesson not found.");
        setLesson(null);
        return;
      }
      setLesson(l);

      const sibs = await fetchModuleLessons(l.module_id);
      setSiblings(sibs);

      if (studentId) {
        // Resolve the student's enrollment in this lesson's cohort.
        const cohortId = l.module?.cohort_id ?? null;
        if (cohortId) {
          const { data: enr } = await supabase
            .from("enrollments")
            .select(
              "id, student_id, cohort_id, completion_status, completed_at",
            )
            .eq("student_id", studentId)
            .eq("cohort_id", cohortId)
            .maybeSingle();
          setEnrollment((enr as EnrollmentRow | null) ?? null);
        }
        const prog = await fetchLessonProgress(studentId, lessonId);
        setCompletedAt(prog?.completed_at ?? null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load lesson.");
    } finally {
      setLoading(false);
    }
  }, [lessonId, studentId]);

  useEffect(() => {
    void load();
  }, [load]);

  const { prev, next } = useMemo(() => {
    const idx = siblings.findIndex((s) => s.id === lessonId);
    return {
      prev: idx > 0 ? siblings[idx - 1] : null,
      next: idx >= 0 && idx < siblings.length - 1 ? siblings[idx + 1] : null,
    };
  }, [siblings, lessonId]);

  async function toggleComplete() {
    if (!studentId || !lessonId) return;
    setSavingProgress(true);
    try {
      if (completedAt) {
        const { error: err } = await unmarkLessonComplete(studentId, lessonId);
        if (err) setError(err);
        else setCompletedAt(null);
      } else {
        const { error: err } = await markLessonComplete(studentId, lessonId);
        if (err) setError(err);
        else setCompletedAt(new Date().toISOString());
      }
    } finally {
      setSavingProgress(false);
    }
  }

  const markdown = lesson ? getMarkdown(lesson.body_ref) : null;
  const assignment = lesson ? getAssignment(lesson.body_ref) : null;
  const isAssignment =
    lesson?.type === "workbook" || lesson?.type === "assignment";

  return (
    <div className="min-h-screen bg-slate-50 text-txcfi-ink">
      <AppHeader />
      <main className="mx-auto max-w-3xl px-6 py-8">
        <Link to="/app" className="text-sm text-slate-500 hover:text-slate-800">
          ← Back to dashboard
        </Link>

        {loading && <p className="mt-6 text-slate-500">Loading…</p>}

        {error && (
          <div className="mt-6 rounded border border-red-200 bg-red-50 p-4 text-red-700">
            {error}
          </div>
        )}

        {!loading && lesson && (
          <>
            <header className="mt-4 mb-2">
              <p className="text-xs uppercase tracking-wide text-slate-400">
                {lesson.module?.block_no
                  ? `Block ${lesson.module.block_no} · `
                  : ""}
                {lesson.type}
              </p>
              <h1 className="text-2xl font-bold text-slate-900">
                {lesson.title}
              </h1>
            </header>

            {/* Content body (prose). Both content and assignment lessons may carry prose. */}
            {markdown ? (
              <article className="mt-4">
                <Markdown>{markdown}</Markdown>
              </article>
            ) : (
              !isAssignment && (
                <div className="mt-4 rounded-lg border border-dashed border-slate-300 bg-white p-6 text-slate-500">
                  No content has been published for this lesson yet.
                </div>
              )
            )}

            {/* Assignment / workbook: question form */}
            {isAssignment && (
              <section className="mt-6">
                {assignment && enrollment ? (
                  <AssignmentForm
                    enrollmentId={enrollment.id}
                    lessonId={lesson.id}
                    assignment={assignment}
                  />
                ) : assignment && !enrollment ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-5 text-amber-800">
                    You need an active enrollment in this cohort to submit
                    answers.
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-slate-500">
                    No workbook questions have been published for this lesson
                    yet.
                  </div>
                )}
              </section>
            )}

            {/* Mark complete (content lessons; assignments mark progress via submit) */}
            {!isAssignment && studentId && (
              <div className="mt-8 flex items-center gap-3">
                <button
                  onClick={() => void toggleComplete()}
                  disabled={savingProgress}
                  className={
                    completedAt
                      ? "rounded border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-60"
                      : "rounded bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-60"
                  }
                >
                  {savingProgress
                    ? "Saving…"
                    : completedAt
                      ? "✓ Completed — mark incomplete"
                      : "Mark lesson complete"}
                </button>
                {completedAt && (
                  <span className="text-sm text-slate-500">
                    Completed {new Date(completedAt).toLocaleDateString()}
                  </span>
                )}
              </div>
            )}

            {/* Prev / next within the module */}
            <nav className="mt-10 flex items-center justify-between border-t border-slate-200 pt-4">
              {prev ? (
                <button
                  onClick={() => navigate(`/app/lesson/${prev.id}`)}
                  className="text-sm text-slate-600 hover:text-slate-900"
                >
                  ← {prev.title}
                </button>
              ) : (
                <span />
              )}
              {next ? (
                <button
                  onClick={() => navigate(`/app/lesson/${next.id}`)}
                  className="text-sm text-slate-600 hover:text-slate-900"
                >
                  {next.title} →
                </button>
              ) : (
                <span />
              )}
            </nav>
          </>
        )}
      </main>
    </div>
  );
}
